import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Button, Select, Table, Tag, Tabs, DatePicker, Alert, Space, Divider, message, Empty, Modal, Form, Input, Checkbox } from 'antd';
import { DownloadOutlined, FileTextOutlined, CalendarOutlined, DollarOutlined, TeamOutlined, BarChartOutlined, BellOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from 'services/api/apiService';

const fmt = (v: any) => `₦${parseFloat(v||0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

const REPORT_TYPES = [
  { key: 'headcount',         label: '👥 Headcount Report',       desc: 'Employee count by department and new hires', icon: <TeamOutlined /> },
  { key: 'payroll_summary',   label: '💰 Payroll Summary',        desc: 'Monthly payroll totals, gross, net, status', icon: <DollarOutlined /> },
  { key: 'paye_schedule',     label: '🏛️ PAYE Schedule (FIRS)',   desc: 'Employee-level PAYE for FIRS filing', icon: <FileTextOutlined /> },
  { key: 'pension_schedule',  label: '🏦 Pension Schedule',       desc: 'Employee + employer pension contributions', icon: <FileTextOutlined /> },
  { key: 'itf_return',        label: '📋 ITF Return',             desc: 'Industrial Training Fund 1% levy by month', icon: <FileTextOutlined /> },
  { key: 'leave_summary',     label: '🌴 Leave Summary',          desc: 'Leave taken by employee and type', icon: <CalendarOutlined /> },
  { key: 'performance_summary', label: '⭐ Performance Summary',   desc: 'Average ratings by department', icon: <BarChartOutlined /> },
  { key: 'document_expiry',   label: '📄 Document Expiry',        desc: 'Documents expiring in next 90 days', icon: <BellOutlined /> },
];

const ReportsDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<any>(null);
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [period, setPeriod] = useState(dayjs().format('YYYY-MM'));
  const [year, setYear] = useState(dayjs().format('YYYY'));
  const [expiryDays, setExpiryDays] = useState(30);
  const [scheduleModal, setScheduleModal] = useState(false);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    const r = await apiService.getReportsDashboard();
    if (r.success) setDashboard(r.data);
  };

  const runReport = async (reportKey: string) => {
    if (!reportKey) { message.warning('Select a report type'); return; }
    setReportLoading(true);
    setReportData(null);
    try {
      const r = await apiService.getReport(reportKey, period, year);
      if (r.success) setReportData({ ...r, reportKey });
      else message.error(r.error || 'Failed to generate report');
    } finally { setReportLoading(false); }
  };

  const exportCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) { message.warning('No data to export'); return; }
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(',')).join('\n');
    const csv = headers + '\n' + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename + '.csv'; a.click();
    URL.revokeObjectURL(url);
    message.success('CSV downloaded');
  };

  const renderReportTable = () => {
    if (!reportData) return null;
    const { reportKey, data, totals, annual, by_department, summary } = reportData;

    switch (reportKey) {
      case 'headcount':
        return (
          <div>
            {data?.new_hires && <Alert message={`New Hires This Period: ${data.new_hires.new_hires || 0}`} type="info" showIcon style={{ marginBottom: 16 }} />}
            <Table dataSource={data?.by_department?.filter((r: any) => r.department)} rowKey="department" pagination={false}
              columns={[
                { title: 'Department', dataIndex: 'department' },
                { title: 'Total', dataIndex: 'total_employees' },
                { title: 'Active', dataIndex: 'active' },
                { title: 'Inactive', dataIndex: 'inactive' },
              ]} />
          </div>
        );

      case 'paye_schedule':
        return (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Card><Statistic title="Total Employees" value={totals?.employee_count || 0} /></Card></Col>
              <Col span={8}><Card><Statistic title="Total Gross" value={totals?.total_gross || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
              <Col span={8}><Card><Statistic title="Total PAYE" value={totals?.total_paye || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
            </Row>
            <Button icon={<DownloadOutlined />} onClick={() => exportCSV(data, `PAYE_Schedule_${period}`)} style={{ marginBottom: 12 }}>Export CSV for FIRS</Button>
            <Table dataSource={data} rowKey="employee_id" pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Employee ID', dataIndex: 'employee_id' },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Department', dataIndex: 'department' },
                { title: 'Gross Pay', dataIndex: 'gross_pay', render: fmt, align: 'right' as const },
                { title: 'PAYE Tax', dataIndex: 'paye_tax', render: fmt, align: 'right' as const },
                { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v==='paid'?'success':'warning'}>{v?.toUpperCase()}</Tag> },
              ]} />
          </div>
        );

      case 'pension_schedule':
        return (
          <div>
            <Button icon={<DownloadOutlined />} onClick={() => exportCSV(data, `Pension_Schedule_${period}`)} style={{ marginBottom: 12 }}>Export CSV for PFA</Button>
            <Table dataSource={data} rowKey="employee_id" pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Name', dataIndex: 'name' },
                { title: 'Department', dataIndex: 'department' },
                { title: 'Gross Pay', dataIndex: 'gross_pay', render: fmt, align: 'right' as const },
                { title: 'Employee Pension (8%)', dataIndex: 'pension_employee', render: fmt, align: 'right' as const },
                { title: 'Employer Pension (10%)', dataIndex: 'pension_employer', render: fmt, align: 'right' as const },
                { title: 'Total', dataIndex: 'total_pension', render: fmt, align: 'right' as const },
              ]} />
          </div>
        );

      case 'itf_return':
        return (
          <div>
            {annual && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}><Card><Statistic title={`${year} Annual Payroll`} value={annual.annual_total || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
                <Col span={12}><Card><Statistic title="Annual ITF Levy (1%)" value={annual.annual_itf || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
              </Row>
            )}
            <Button icon={<DownloadOutlined />} onClick={() => exportCSV(data, `ITF_Return_${year}`)} style={{ marginBottom: 12 }}>Export ITF Return</Button>
            <Table dataSource={data} rowKey="period" pagination={false}
              columns={[
                { title: 'Period', dataIndex: 'period' },
                { title: 'Employees', dataIndex: 'employee_count' },
                { title: 'Monthly Payroll', dataIndex: 'annual_payroll', render: fmt, align: 'right' as const },
                { title: 'ITF Levy (1%)', dataIndex: 'itf_levy', render: fmt, align: 'right' as const },
              ]} />
          </div>
        );

      case 'leave_summary':
        return (
          <div>
            <Button icon={<DownloadOutlined />} onClick={() => exportCSV(data, `Leave_Summary_${year}`)} style={{ marginBottom: 12 }}>Export CSV</Button>
            <Table dataSource={data} rowKey={(r: any) => `${r.employee_name}_${r.leave_type}`} pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Employee', dataIndex: 'first_name', render: (_: any, r: any) => `${r.first_name} ${r.last_name}` },
                { title: 'Department', dataIndex: 'department' },
                { title: 'Leave Type', dataIndex: 'leave_type', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Days Taken', dataIndex: 'days_taken' },
                { title: 'Approved', dataIndex: 'approved', render: (v: number) => <Tag color="success">{v}</Tag> },
                { title: 'Pending', dataIndex: 'pending', render: (v: number) => v > 0 ? <Tag color="warning">{v}</Tag> : '-' },
              ]} />
          </div>
        );

      case 'payroll_summary':
        return (
          <div>
            <Button icon={<DownloadOutlined />} onClick={() => exportCSV(data, `Payroll_Summary_${year}`)} style={{ marginBottom: 12 }}>Export CSV</Button>
            <Table dataSource={data} rowKey="period" pagination={false}
              columns={[
                { title: 'Period', dataIndex: 'period' },
                { title: 'Employees', dataIndex: 'employee_count' },
                { title: 'Total Gross', dataIndex: 'total_gross', render: fmt, align: 'right' as const },
                { title: 'Total PAYE', dataIndex: 'total_paye', render: fmt, align: 'right' as const },
                { title: 'Total Net', dataIndex: 'total_net', render: fmt, align: 'right' as const },
                { title: 'Paid', dataIndex: 'paid_count', render: (v: number) => <Tag color="success">{v}</Tag> },
                { title: 'Draft', dataIndex: 'draft_count', render: (v: number) => v > 0 ? <Tag color="warning">{v}</Tag> : '-' },
              ]} />
          </div>
        );

      case 'performance_summary':
        return (
          <div>
            <Table dataSource={data} rowKey="department" pagination={false}
              columns={[
                { title: 'Department', dataIndex: 'department' },
                { title: 'Reviews', dataIndex: 'review_count' },
                { title: 'Avg Rating', dataIndex: 'avg_rating', render: (v: number) => <Tag color={v>=4?'success':v>=3?'processing':'warning'}>{v}/5</Tag> },
                { title: 'High Performers', dataIndex: 'high_performers', render: (v: number) => <Tag color="success">{v}</Tag> },
                { title: 'Low Performers', dataIndex: 'low_performers', render: (v: number) => v > 0 ? <Tag color="error">{v}</Tag> : '-' },
              ]} />
          </div>
        );

      case 'document_expiry':
        return (
          <div>
            {reportData.already_expired?.length > 0 && (
              <Alert message={`${reportData.already_expired.length} documents already expired — marked as expired`} type="error" showIcon style={{ marginBottom: 16 }} />
            )}
            <Table dataSource={data} rowKey="id" pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Document', dataIndex: 'title' },
                { title: 'Employee', dataIndex: 'employee_name' },
                { title: 'Department', dataIndex: 'department' },
                { title: 'Type', dataIndex: 'document_type', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Expiry Date', dataIndex: 'expiry_date' },
                { title: 'Days Left', dataIndex: 'days_until_expiry', render: (v: number) => <Tag color={v<=7?'error':v<=14?'warning':'processing'}>{v} days</Tag> },
              ]} />
          </div>
        );

      default:
        return <pre style={{ fontSize: 11 }}>{JSON.stringify(reportData, null, 2)}</pre>;
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1>Reports & Analytics</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>Generate statutory reports, payroll schedules, and HR analytics</p>

      {/* Dashboard summary */}
      {dashboard && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}><Card><Statistic title="Active Employees" value={dashboard.active_employees || 0} prefix={<TeamOutlined />} /></Card></Col>
          <Col span={4}><Card><Statistic title="Pending Leave" value={dashboard.pending_leave || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Draft Payroll" value={dashboard.draft_payroll || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Open Tickets" value={dashboard.open_tickets || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Current Payroll" value={dashboard.current_payroll || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
          <Col span={4}><Card><Statistic title="Expiring Docs (30d)" value={dashboard.expiring_docs || 0} valueStyle={{ color: dashboard.expiring_docs > 0 ? '#ff4d4f' : 'inherit' }} /></Card></Col>
        </Row>
      )}

      {/* Report Builder */}
      <Card title="📊 Report Builder" extra={
        <Button icon={<SettingOutlined />} onClick={() => setScheduleModal(true)}>Schedule Reports</Button>
      }>
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {REPORT_TYPES.map(rt => (
            <Col span={6} key={rt.key} style={{ marginBottom: 12 }}>
              <Card
                size="small"
                hoverable
                style={{ border: selectedReport === rt.key ? '2px solid #6366f1' : '1px solid #f0f0f0', cursor: 'pointer' }}
                onClick={() => setSelectedReport(rt.key)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{rt.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{rt.label}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{rt.desc}</div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {selectedReport && (
          <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <Space wrap>
              {['paye_schedule','pension_schedule','leave_summary','payroll_summary','headcount'].includes(selectedReport) && (
                <DatePicker picker="month" value={dayjs(period)} onChange={d => setPeriod(d ? d.format('YYYY-MM') : dayjs().format('YYYY-MM'))}
                  placeholder="Select Month" />
              )}
              {['itf_return','leave_summary','payroll_summary','performance_summary'].includes(selectedReport) && (
                <DatePicker picker="year" value={dayjs(year)} onChange={d => setYear(d ? d.format('YYYY') : dayjs().format('YYYY'))}
                  placeholder="Select Year" />
              )}
              {selectedReport === 'document_expiry' && (
                <Select value={expiryDays} onChange={setExpiryDays} style={{ width: 160 }}>
                  <Select.Option value={7}>Next 7 days</Select.Option>
                  <Select.Option value={14}>Next 14 days</Select.Option>
                  <Select.Option value={30}>Next 30 days</Select.Option>
                  <Select.Option value={90}>Next 90 days</Select.Option>
                </Select>
              )}
              <Button type="primary" icon={<BarChartOutlined />} loading={reportLoading}
                onClick={() => runReport(selectedReport)}>
                Generate Report
              </Button>
            </Space>
          </div>
        )}

        {reportLoading && <div style={{ textAlign: 'center', padding: 40 }}>Generating report...</div>}
        {reportData && !reportLoading && (
          <div>
            <Divider>{REPORT_TYPES.find(r => r.key === reportData.reportKey)?.label}</Divider>
            {renderReportTable()}
          </div>
        )}
        {!selectedReport && !reportLoading && <Empty description="Select a report type above to get started" />}
      </Card>

      {/* Schedule Reports Modal */}
      <Modal title="⏰ Schedule Automated Reports" open={scheduleModal} onCancel={() => setScheduleModal(false)} footer={null} width={560}>
        <Alert message="Scheduled reports are automatically sent to HR admin on the 1st of each month and include payroll summary, leave summary, and document expiry alerts." type="info" showIcon style={{ marginBottom: 16 }} />
        <div style={{ marginBottom: 12 }}>
          <strong>Currently Scheduled:</strong>
          <ul style={{ marginTop: 8 }}>
            <li>🕐 Daily 8:00 AM — Document expiry alerts (30, 14, 7, 1 day warnings)</li>
            <li>🗓️ 1st of month 7:00 AM — Monthly HR summary report to admin</li>
          </ul>
        </div>
        <Alert message="To customise schedule frequency or recipients, update the cron configuration on the server." type="warning" showIcon />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button type="primary" onClick={() => setScheduleModal(false)}>Close</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ReportsDashboard;
